// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Clatter — bootstrap and the screen machine. Owns which screen is up, the room's Net (created ONCE
// per session and never left for a rematch), the two-tap machine that turns a die and a track into
// one packed action, and the setInterval that drives every clock. Rules live in rules.ts, the round
// in match.ts, the sheet in render.ts, the copy in ui.ts.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type RoundPlayer, type Rounds } from '@ben-gy/game-engine/rematch';
import { clearRoomInUrl, createLobby, createRoomEntry, normalizeRoomCode, setRoomInUrl } from '@ben-gy/game-engine/lobby';
import { resolveName } from '@ben-gy/game-engine/identity';
import { newSeed } from '@ben-gy/game-engine/rng';

import { escapeHtml } from './dom';
import { MAX_PLAYERS, MODE_IDS, modeOf } from './modes';
import { DEFAULT_TIER, TIER_IDS, tierOf } from './bot';
import {
  DIE_NONE,
  PH_ACTIVE,
  SHIM_DOWN,
  SHIM_NONE,
  SHIM_UP,
  T_NONE,
  T_WEIGHTS,
  TRACK_NAME,
  aDie,
  aShim,
  aSlot,
  aTrack,
  activeSeat,
  shimDelta,
  type PlyEvent,
} from './rules';
import { Match, seatForRound, type WireMsg } from './match';
import { dieHtml, emptyHints, scoreStripHtml, sheetHtml, shimsHtml } from './render';
import { runCountdown } from './countdown';
import { missedNote } from './analysis';
import { ATTRIB, aboutHtml, helpHtml, menuHtml, resultsHtml, type ResultRow } from './ui';
import { SEAT_NAME } from './palette';

const SLUG = 'clatter';

const app = document.querySelector<HTMLElement>('#app')!;
let content: HTMLElement;

const store = createStore(SLUG);
const sfx = createSfx({
  muted: store.get<boolean>('muted', false) ?? false,
  patches: {
    roll: { type: 'square', freq: [180, 90], dur: 0.16, gain: 0.22, noise: true },
    mark: { type: 'triangle', freq: [560, 900], dur: 0.08, gain: 0.24 },
    chain: { type: 'square', freq: [700, 1200], dur: 0.11, gain: 0.22 },
    spill: { type: 'sawtooth', freq: [320, 140], dur: 0.2, gain: 0.2, noise: true },
  },
});
const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

let unlocked = false;
function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  try {
    sfx.unlock();
  } catch {
    /* audio unlock is best-effort */
  }
}
window.addEventListener('pointerdown', unlock, { once: true });
window.addEventListener('keydown', unlock, { once: true });

const playerName = resolveName(store, () => `Player ${Math.floor(Math.random() * 900 + 100)}`);
let myModeChoice = modeOf(store.get<string>('mode', '')).id;
let hostOptsMode = myModeChoice;
let myTier = tierOf(store.get<string>('tier', DEFAULT_TIER)).id;
let soloPlayers = Math.max(2, Math.min(MAX_PLAYERS, store.get<number>('players', 2) ?? 2));

const bestKey = (id: string): string => `best_${id}`;
const bestFor = (id: string): number => store.get<number>(bestKey(id), 0) ?? 0;
function noteBest(id: string, v: number): void {
  if (v > bestFor(id)) store.set(bestKey(id), v);
}

let net: Net | null = null;
let rounds: Rounds | null = null;
let match: Match | null = null;
let roster: RoundPlayer[] = [];
let tally: number[] = [];
let roomCode = '';
let roundNo = 0;
let soloRound = 0;

// ── screen lifecycle ────────────────────────────────────────────────────────────
const cleanups: Array<() => void> = [];
const onCleanup = (fn: () => void): void => void cleanups.push(fn);
function teardown(): void {
  for (const fn of cleanups.splice(0)) {
    try {
      fn();
    } catch {
      /* a failing teardown must never block the next screen */
    }
  }
}
const setPlaying = (on: boolean): void => void document.body.classList.toggle('playing', on);

function mountShell(): void {
  app.innerHTML = `<main class="main-content" id="content"></main><footer class="site-footer">${ATTRIB}</footer>`;
  content = document.getElementById('content')!;
}

function sheetPanel(html: string): void {
  const wrap = document.createElement('div');
  wrap.className = 'overlay';
  wrap.innerHTML = `<div class="sheet-panel">${html}</div>`;
  document.body.appendChild(wrap);
  const close = (): void => wrap.remove();
  const armedAt = Date.now();
  // A panel opened from a pointerup would otherwise be closed by that same gesture's trailing click.
  wrap.addEventListener('pointerdown', (e) => {
    if (e.target === wrap && Date.now() - armedAt > 350) close();
  });
  wrap.querySelectorAll('.sheet-close').forEach((b) => b.addEventListener('click', close));
  const esc = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      close();
      window.removeEventListener('keydown', esc);
    }
  };
  window.addEventListener('keydown', esc);
}

function toast(msg: string): void {
  const t = document.createElement('div');
  t.className = 'toast';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.classList.add('show'), 10);
  setTimeout(() => t.remove(), 1900);
}

function ping(cue: string, pitch?: number): void {
  try {
    sfx.play(cue, pitch === undefined ? undefined : { pitch });
  } catch {
    /* audio is best-effort and must never break a turn */
  }
}

// ── menu ────────────────────────────────────────────────────────────────────────
function showMenu(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = menuHtml({
    modeId: myModeChoice,
    tierId: myTier,
    players: soloPlayers,
    best: bestFor(myModeChoice),
    muted: sfx.muted(),
  });

  content.querySelectorAll<HTMLElement>('.chip[data-mode]').forEach((c) =>
    c.addEventListener('click', () => {
      myModeChoice = modeOf(c.dataset.mode).id;
      store.set('mode', myModeChoice);
      ping('select');
      showMenu();
    }),
  );
  content.querySelectorAll<HTMLElement>('.chip[data-tier]').forEach((c) =>
    c.addEventListener('click', () => {
      myTier = TIER_IDS.includes(c.dataset.tier ?? '') ? (c.dataset.tier as string) : DEFAULT_TIER;
      store.set('tier', myTier);
      ping('select');
      showMenu();
    }),
  );
  content.querySelectorAll<HTMLElement>('.chip[data-players]').forEach((c) =>
    c.addEventListener('click', () => {
      soloPlayers = Math.max(2, Math.min(MAX_PLAYERS, Number(c.dataset.players) || 2));
      store.set('players', soloPlayers);
      ping('select');
      showMenu();
    }),
  );
  content.querySelector('#playSolo')!.addEventListener('click', () => startSolo());
  content.querySelector('#playFriends')!.addEventListener('click', () => showRoomEntry());
  content.querySelector('#help')!.addEventListener('click', () => sheetPanel(helpHtml()));
  content.querySelector('#about')!.addEventListener('click', () => sheetPanel(aboutHtml()));
  content.querySelector('#mute')!.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    showMenu();
  });

  if (!store.get<boolean>('seenHelp', false)) {
    store.set('seenHelp', true);
    sheetPanel(helpHtml());
  }
}

// ── the game screen ─────────────────────────────────────────────────────────────
interface Screen {
  destroy(): void;
}

let activeMatch: Match | null = null;
let activeSubmit: ((action: number) => boolean) | null = null;

function buildGameScreen(m: Match, onQuit: () => void, onOver: () => void): Screen {
  content.innerHTML = `
    <section class="screen game">
      <div class="hud">
        <span class="hud-turn" id="hudTurn"></span>
        <span class="hud-mode">${escapeHtml(m.mode.name)}</span>
        <button class="iconbtn" id="pause" title="Pause" aria-label="Pause">❚❚</button>
      </div>
      <div class="clockbar" id="clockbar" hidden><i id="clockfill"></i></div>
      <div class="strip-host" id="strip"></div>
      <div class="tray-host" id="trayHost">
        <p class="tray-label" id="trayLabel"></p>
        <div class="tray" id="tray"></div>
        <div class="tray-side" id="traySide"></div>
      </div>
      <p class="prompt" id="prompt" aria-live="polite"></p>
      <div class="sheet-host" id="sheetHost"></div>
      <div class="turn-actions" id="actions"></div>
    </section>`;

  const hudTurn = content.querySelector<HTMLElement>('#hudTurn')!;
  const clockbar = content.querySelector<HTMLElement>('#clockbar')!;
  const clockfill = content.querySelector<HTMLElement>('#clockfill')!;
  const strip = content.querySelector<HTMLElement>('#strip')!;
  const trayLabel = content.querySelector<HTMLElement>('#trayLabel')!;
  const tray = content.querySelector<HTMLElement>('#tray')!;
  const traySide = content.querySelector<HTMLElement>('#traySide')!;
  const promptEl = content.querySelector<HTMLElement>('#prompt')!;
  const sheetHost = content.querySelector<HTMLElement>('#sheetHost')!;
  const actions = content.querySelector<HTMLElement>('#actions')!;

  let selDie: number | null = null;
  let armedShim = SHIM_NONE;
  let live = false;
  let destroyed = false;
  let fresh = new Set<string>();
  let botTimer: ReturnType<typeof setTimeout> | null = null;

  const mySeat = (): number => m.mySeat ?? 0;

  const cd = runCountdown(sheetHost, sfx, () => {
    if (destroyed) return;
    live = true;
    ping('roll');
    paint();
    maybeBots();
  });

  /** Actions available for the die + shim the player currently has picked up. */
  function candidates(): number[] {
    if (!live || !m.mine || selDie === null) return [];
    return m.legal().filter((a) => aDie(a) === selDie && aShim(a) === armedShim && aTrack(a) !== T_NONE);
  }

  function liveKeys(): Set<string> {
    const out = new Set<string>();
    for (const a of candidates()) out.add(aTrack(a) === T_WEIGHTS ? `${T_WEIGHTS}:${aSlot(a)}` : String(aTrack(a)));
    return out;
  }

  function paint(): void {
    if (destroyed) return;
    const s = m.state;
    const seat = mySeat();

    hudTurn.innerHTML = m.fault
      ? '<span class="fault-note">Round stopped</span>'
      : m.over
        ? '<span>Round over</span>'
        : `<span class="rtok s${activeSeat(s)}"></span><span>Turn ${Math.min(s.turn + 1, m.mode.turns)}/${m.mode.turns}</span>`;

    strip.innerHTML = scoreStripHtml(s.sheets, m.mode, m.names, m.mySeat, m.waitingOn);
    strip.querySelectorAll<HTMLElement>('[data-peek]').forEach((b) =>
      b.addEventListener('click', () => {
        const who = Number(b.dataset.peek);
        if (who === m.mySeat) return;
        const hints = emptyHints();
        hints.compact = true;
        sheetPanel(
          `<h2>${escapeHtml(m.names[who] ?? `Player ${who + 1}`)}</h2><button class="sheet-close x" aria-label="Close">✕</button>${sheetHtml(
            s.sheets[who],
            m.mode,
            hints,
          )}`,
        );
      }),
    );

    renderTray();

    const hints = emptyHints();
    hints.interactive = live && m.mine && selDie !== null;
    hints.live = liveKeys();
    hints.fresh = fresh;
    sheetHost.innerHTML = sheetHtml(s.sheets[seat], m.mode, hints);
    sheetHost.querySelectorAll<HTMLElement>('[data-place]').forEach((b) =>
      b.addEventListener('click', () => {
        const [t, slot] = (b.dataset.place ?? '').split(':');
        commit(Number(t), Number(slot ?? 0));
      }),
    );

    promptEl.textContent = promptText();
    renderClock();
    renderActions();
  }

  function renderTray(): void {
    const s = m.state;
    const activeNow = s.phase === PH_ACTIVE;
    const dice = activeNow ? s.pool : s.spill;
    const iAmActive = m.mySeat !== null && activeSeat(s) === m.mySeat;
    const canAct = live && m.mine;

    trayLabel.textContent = m.over
      ? ''
      : activeNow
        ? iAmActive
          ? 'Your roll — take one'
          : `${m.names[activeSeat(s)] ?? 'They'} are choosing…`
        : dice.length === 0
          ? 'Nothing spilled'
          : canAct
            ? 'The spill — take one'
            : 'The spill';

    const takenFace = !activeNow && s.takenIdx >= 0 ? s.pool[s.takenIdx] : -1;
    const chosen = selDie !== null && selDie < dice.length ? dice[selDie] : -1;

    const cells = dice
      .map((face, i) => {
        const canon = dice.findIndex((f) => f === face);
        const legalHere = canAct && m.legal().some((a) => aDie(a) === canon);
        const isSel = selDie !== null && dice[selDie] === face;
        const shifted = isSel && armedShim !== SHIM_NONE ? face + shimDelta(armedShim) : undefined;
        // The teaching moment: while you hold a die, the ones it would send to the table are marked.
        const willSpill = canAct && activeNow && chosen > 0 && face < chosen;
        return dieHtml(face, {
          action: String(i),
          selected: isSel,
          spilling: willSpill,
          disabled: !legalHere,
          shifted,
          label: `${face}${willSpill ? ' — would go to the table' : ''}`,
        });
      })
      .join('');

    tray.innerHTML =
      dice.length === 0
        ? `<p class="tray-empty">${activeNow ? '' : takenFace > 0 ? `Nothing was smaller than the ${takenFace}.` : ''}</p>`
        : cells;
    tray.classList.toggle('spilled', !activeNow);
    tray.querySelectorAll<HTMLElement>('[data-die]').forEach((b) =>
      b.addEventListener('click', () => pickDie(Number(b.dataset.die))),
    );

    traySide.innerHTML = canAct || m.state.sheets[mySeat()].shims > 0 ? shimsHtml(m.state.sheets[mySeat()], armedShim, canAct) : '';
    traySide.querySelectorAll<HTMLElement>('[data-shim]').forEach((b) =>
      b.addEventListener('click', () => {
        const dir = Number(b.dataset.shim);
        armedShim = armedShim === dir ? SHIM_NONE : dir;
        ping('blip', armedShim === SHIM_NONE ? 0.8 : 1.2);
        paint();
      }),
    );
  }

  function promptText(): string {
    if (m.fault === 'build') return 'Someone is on a different build of Clatter — reload to play together.';
    if (m.fault === 'desync') return 'The sheets stopped agreeing, so the round was stopped.';
    if (m.over) return '';
    if (!live) return '';
    if (m.mySeat === null) return 'Watching — you are in the next round.';
    if (!m.mine) {
      const waiting = m.waitingOn.map((i) => m.names[i] ?? `Player ${i + 1}`);
      return waiting.length ? `Waiting for ${waiting.join(', ')}…` : '';
    }
    const acts = m.legal();
    if (acts.length === 1 && aDie(acts[0]) === DIE_NONE) return 'Nothing here fits your sheet — take a shim instead.';
    if (acts.every((a) => aTrack(a) === T_NONE)) return 'No die has a home — take one anyway and bank a shim.';
    if (selDie === null) return m.state.phase === PH_ACTIVE ? 'Take a die. Everything smaller spills.' : 'Take one from the spill.';
    return 'Now tap a track.';
  }

  function renderClock(): void {
    if (m.clockMs <= 0 || m.over || !m.mine) {
      clockbar.hidden = true;
      return;
    }
    clockbar.hidden = false;
    const frac = Math.max(0, Math.min(1, m.decisionLeft / m.clockMs));
    clockfill.style.width = `${frac * 100}%`;
    clockbar.classList.toggle('low', frac < 0.25);
  }

  function renderActions(): void {
    const canPass = live && m.mine && m.legal().some((a) => aTrack(a) === T_NONE);
    const canBack = selDie !== null || armedShim !== SHIM_NONE;
    actions.innerHTML = `${canBack ? '<button class="btn" id="back">Back</button>' : ''}${
      canPass ? '<button class="btn" id="pass">Take a shim</button>' : ''
    }<button class="btn ghost" id="quit">Quit</button>`;
    actions.querySelector('#back')?.addEventListener('click', () => {
      if (armedShim !== SHIM_NONE) armedShim = SHIM_NONE;
      else selDie = null;
      ping('blip', 0.7);
      paint();
    });
    actions.querySelector('#pass')?.addEventListener('click', () => {
      const a = m.legal().find((x) => aTrack(x) === T_NONE && (selDie === null || aDie(x) === selDie)) ?? m.legal().find((x) => aTrack(x) === T_NONE);
      if (a !== undefined) submit(a);
    });
    actions.querySelector('#quit')!.addEventListener('click', onQuit);
  }

  /**
   * Two dice showing the same face are the SAME decision — legalActions only offers the first, so a
   * raw index would render the duplicate as a dead die the player can see but not touch. Resolve
   * every tap to the first index of that face instead.
   */
  function canonDie(i: number): number {
    const dice = m.state.phase === PH_ACTIVE ? m.state.pool : m.state.spill;
    const face = dice[i];
    const first = dice.findIndex((f) => f === face);
    return first < 0 ? i : first;
  }

  function pickDie(raw: number): void {
    if (!live || !m.mine) return;
    const i = canonDie(raw);
    if (!m.legal().some((a) => aDie(a) === i)) {
      ping('hit', 0.6);
      return;
    }
    selDie = selDie === i ? null : i;
    if (selDie === null) armedShim = SHIM_NONE;
    ping('select');
    paint();
    // One legal home and no shim armed? Then the second tap is a formality — but never auto-play
    // it, because a player who cannot see WHERE it went learns nothing about their own sheet.
  }

  function commit(track: number, slot: number): void {
    const a = candidates().find((x) => aTrack(x) === track && (track !== T_WEIGHTS || aSlot(x) === slot));
    if (a === undefined) {
      ping('hit', 0.6);
      return;
    }
    submit(a);
  }

  function submit(action: number): boolean {
    if (!m.submit(action)) return false;
    selDie = null;
    armedShim = SHIM_NONE;
    paint();
    maybeBots();
    return true;
  }
  activeSubmit = submit;

  function juice(events: PlyEvent[]): void {
    const seat = mySeat();
    fresh = new Set();
    let chain = 0;
    for (const e of events) {
      if (e.kind === 'write' && e.seat === seat) {
        fresh.add(`${e.track}:${e.cell}`);
        if (e.free) {
          chain++;
          ping('chain', 1 + chain * 0.12);
        } else ping('mark');
      } else if (e.kind === 'spill' && (e.n ?? 0) > 0) {
        ping('spill', 0.9);
      } else if (e.kind === 'complete' && e.seat === seat) {
        ping('win');
        sheetHost.classList.add('flash');
        setTimeout(() => sheetHost.classList.remove('flash'), 420);
        if (!reducedMotion) shake();
      } else if (e.kind === 'shim' && e.seat === seat) {
        ping('coin', 1.1);
      } else if (e.kind === 'tip' && e.seat === seat) {
        ping('coin', 0.85);
      }
      if (!reducedMotion && 'vibrate' in navigator && e.seat === seat && e.kind === 'complete') {
        try {
          navigator.vibrate([12, 30, 12]);
        } catch {
          /* haptics are best-effort */
        }
      }
    }
    setTimeout(() => {
      fresh = new Set();
    }, 700);
  }

  function shake(): void {
    sheetHost.classList.add('shaking');
    setTimeout(() => sheetHost.classList.remove('shaking'), 320);
  }

  function maybeBots(): void {
    if (destroyed || !live || m.over || m.botTier === null) return;
    if (botTimer !== null) return;
    // setTimeout, never rAF: a backgrounded tab throttles rAF to nothing and the opponents' turns
    // would simply never arrive.
    botTimer = setTimeout(() => {
      botTimer = null;
      if (destroyed || m.over) return;
      if (m.playBots()) {
        paint();
        maybeBots();
      }
    }, 320);
  }

  // Measure the REAL elapsed time rather than trusting the interval: a backgrounded tab clamps
  // timers to about once a second, so a hard-coded dt makes every clock run at a fraction of speed.
  let lastTick = Date.now();
  const clock = setInterval(() => {
    if (destroyed) return;
    const now = Date.now();
    const dt = Math.min(5000, Math.max(0, now - lastTick));
    lastTick = now;
    if (live) m.tick(dt);
  }, 250);

  function setPaused(on: boolean): void {
    let ov = sheetHost.querySelector<HTMLElement>('.pause-ov');
    if (on) {
      if (ov) return;
      ov = document.createElement('div');
      ov.className = 'pause-ov';
      ov.innerHTML = `<div class="pause-card"><h2>Paused</h2>
        <button class="btn primary" id="resume">Resume</button>
        <button class="btn ghost" id="pquit">Quit</button></div>`;
      sheetHost.appendChild(ov);
      ov.querySelector('#resume')!.addEventListener('click', () => setPaused(false));
      ov.querySelector('#pquit')!.addEventListener('click', onQuit);
    } else {
      ov?.remove();
    }
  }
  content.querySelector('#pause')!.addEventListener('click', () => setPaused(!sheetHost.querySelector('.pause-ov')));

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'p' || e.key === 'P') {
      setPaused(!sheetHost.querySelector('.pause-ov'));
      return;
    }
    if (e.key === 'Escape') {
      if (armedShim !== SHIM_NONE) armedShim = SHIM_NONE;
      else selDie = null;
      paint();
      return;
    }
    if (e.key >= '1' && e.key <= '6') {
      pickDie(Number(e.key) - 1);
      return;
    }
    if (e.key === 's' || e.key === 'S') {
      armedShim = armedShim === SHIM_UP ? SHIM_DOWN : armedShim === SHIM_DOWN ? SHIM_NONE : SHIM_UP;
      paint();
      return;
    }
    const track = { a: 0, t: 1, r: 2, w: 3 }[e.key.toLowerCase()];
    if (track !== undefined) {
      const a = candidates().find((x) => aTrack(x) === track);
      if (a !== undefined) submit(a);
    }
  }
  window.addEventListener('keydown', onKey);

  m.deps.onChange = paint;
  m.deps.onEvents = juice;
  m.deps.onEnd = () => {
    setTimeout(() => {
      if (!destroyed) onOver();
    }, 900);
  };

  paint();

  return {
    destroy() {
      destroyed = true;
      clearInterval(clock);
      if (botTimer !== null) clearTimeout(botTimer);
      cd.cancel();
      window.removeEventListener('keydown', onKey);
      if (activeSubmit === submit) activeSubmit = null;
    },
  };
}

function runRound(m: Match, onQuit: () => void, onOver: () => void): void {
  const screen = buildGameScreen(m, onQuit, onOver);
  onCleanup(() => screen.destroy());
}

// ── solo ────────────────────────────────────────────────────────────────────────
function startSolo(): void {
  teardown();
  setPlaying(true);
  soloRound++;
  const mode = modeOf(myModeChoice);
  const n = soloPlayers;
  const tier = tierOf(myTier);
  const names = Array.from({ length: n }, (_, i) => (i === 0 ? playerName : `${tier.name} ${SEAT_NAME[i]}`));
  const m = new Match({
    modeId: mode.id,
    seed: newSeed(),
    round: soloRound,
    names,
    n,
    mySeat: 0,
    botTier: tier,
    deps: { send: () => {}, onChange: () => {}, onEvents: () => {}, onEnd: () => {} },
  });
  match = m;
  activeMatch = m;
  tally = new Array<number>(n).fill(0);
  runRound(
    m,
    () => showMenu(),
    () => showResults(false),
  );
}

// ── multiplayer ─────────────────────────────────────────────────────────────────
let netSend: (msg: WireMsg) => void = () => {};
let mvOff: () => void = () => {};
let lobby: { repaint(): void; destroy(): void } | null = null;
let lobbyMounted = false;
let beat: ReturnType<typeof setInterval> | null = null;

function repaintLobby(): void {
  if (lobbyMounted) lobby?.repaint();
}

function showRoomEntry(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = '<section class="screen"><div id="entry"></div></section>';
  const host = content.querySelector<HTMLElement>('#entry')!;

  const deep = new URL(location.href).searchParams.get('room');
  if (deep) {
    const code = normalizeRoomCode(deep);
    // Honoured ONCE, and cleared on the way in — otherwise a reload silently drags the player back
    // into a room they deliberately left.
    clearRoomInUrl();
    if (code.length >= 3) {
      void enterRoom(code, false);
      return;
    }
  }

  const entry = createRoomEntry({
    container: host,
    onSubmit: (code: string, created: boolean) => void enterRoom(normalizeRoomCode(code), created),
    onCancel: showMenu,
    title: 'Play with friends',
    subtitle: 'Start a room and send the link, or type a code you were given. Up to four of you.',
  });
  onCleanup(() => entry.destroy());
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  teardown();
  setPlaying(false);
  roomCode = code;
  setRoomInUrl(code);
  tally = [];
  match = null;
  activeMatch = null;

  content.innerHTML = `<section class="screen"><div class="lobby-searching"><span class="spinner"></span> Connecting…</div></section>`;

  try {
    // ONCE at boot, before ANY mesh: Trystero builds its connection pool from the first joinRoom's
    // config, so a later call leaves half of every pair STUN-only.
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail open — TURN is an upgrade, never a dependency */
  }

  const n = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      onHostChange: () => repaintLobby(),
      onPeers: () => {
        repaintLobby();
        match?.peerBack();
      },
      onPeerLeave: (id) => {
        const idx = roster.findIndex((p) => p.id === id);
        if (idx >= 0 && match) match.peerLeft(seatForRound(idx, roundNo, match.n));
        repaintLobby();
      },
    },
  );
  net = n;

  const r = createRounds<{ mode: string }>({
    net: n,
    playerName,
    minPlayers: 2,
    roundOpts: () => ({ mode: myModeChoice }),
    onRound: (info) => startNetRound(info),
    onChange: (s) => {
      const ho = s.hostOpts as { mode?: string } | null;
      if (ho && typeof ho.mode === 'string') hostOptsMode = modeOf(ho.mode).id;
      repaintLobby();
      refreshResults();
    },
  });
  rounds = r;

  const send = n.channel<WireMsg>('mv', (data) => match?.receive(data));
  netSend = (msg) => send(msg);
  mvOff = () => send.off();

  showLobby();
}

function showLobby(): void {
  const n = net;
  const r = rounds;
  if (!n || !r) return;
  lobbyMounted = true;
  setPlaying(false);

  content.innerHTML = `<section class="screen"><div id="lobby-host"></div></section>`;
  const isHost = (): boolean => n.isHost() && n.hostSettled();

  lobby = createLobby({
    container: content.querySelector<HTMLElement>('#lobby-host')!,
    net: n,
    rounds: r,
    roomCode,
    minPlayers: 2,
    maxPlayers: MAX_PLAYERS,
    onCancel: () => void leaveRoom(),
    modeSlot: () =>
      isHost()
        ? `<div class="lobby-modes" id="lm">${MODE_IDS.map(
            (id) =>
              `<button class="chip mode-btn${id === myModeChoice ? ' on' : ''}" data-mode="${id}" aria-pressed="${
                id === myModeChoice
              }">${escapeHtml(modeOf(id).name)}</button>`,
          ).join('')}<p class="mode-blurb">${escapeHtml(modeOf(myModeChoice).blurb)}</p></div>`
        : `<p class="host-pick">${
            n.hostSettled() ? `The host chose <strong>${escapeHtml(modeOf(hostOptsMode).name)}</strong>` : 'Waiting for the host…'
          }</p>`,
    onModeMount: () => {
      if (!isHost()) return;
      content.querySelectorAll<HTMLElement>('#lm .mode-btn').forEach((b) =>
        b.addEventListener('click', () => {
          myModeChoice = modeOf(b.dataset.mode).id;
          store.set('mode', myModeChoice);
          lobby?.repaint();
        }),
      );
    },
  });
  onCleanup(() => {
    lobby?.destroy();
    lobby = null;
  });
}

function startNetRound(info: RoundInfo<{ mode: string }>): void {
  teardown();
  lobbyMounted = false;
  roster = info.players;
  roundNo = info.round;
  const mode = modeOf(info.opts?.mode);
  const n = Math.max(2, Math.min(MAX_PLAYERS, roster.length));
  const myIndex = roster.findIndex((p) => p.id === (net?.selfId ?? ''));
  const seated = info.seated && myIndex >= 0 && myIndex < n;
  const mySeat = seated ? seatForRound(myIndex, info.round, n) : null;

  const names = new Array<string>(n).fill('');
  roster.slice(0, n).forEach((p, i) => {
    const seat = seatForRound(i, info.round, n);
    // Two players CAN arrive with the same handle — the default is random and two tabs on one
    // device share the store outright. Identical names make a results screen unreadable.
    names[seat] = p.name || SEAT_NAME[seat];
  });
  names.forEach((v, i) => {
    if (!v) names[i] = SEAT_NAME[i];
  });
  // Count FIRST: tagging in place makes the second duplicate look unique by the time it is checked,
  // so only one of the pair ever got its seat name and the results screen still read as two
  // identical players. Two tabs on one device share the store, so this is the common case.
  const counts = new Map<string, number>();
  for (const v of names) counts.set(v, (counts.get(v) ?? 0) + 1);
  names.forEach((v, i) => {
    if ((counts.get(v) ?? 0) > 1) names[i] = `${v} (${SEAT_NAME[i]})`;
  });

  if (tally.length !== n) tally = new Array<number>(n).fill(0);

  const m = new Match({
    modeId: mode.id,
    seed: info.seed,
    round: info.round,
    names,
    n,
    mySeat,
    botTier: null,
    clockMs: mode.clockMs,
    deps: { send: (msg) => netSend(msg), onChange: () => {}, onEvents: () => {}, onEnd: () => {} },
  });
  match = m;
  activeMatch = m;
  setPlaying(true);

  if (beat !== null) clearInterval(beat);
  // The heartbeat is what makes a lost message a non-event: it re-sends the whole log every 2s.
  beat = setInterval(() => m.broadcast(), 2000);
  onCleanup(() => {
    if (beat !== null) clearInterval(beat);
    beat = null;
  });

  runRound(
    m,
    () => {
      // End this peer's round FIRST (phase playing → waiting), or the engine lobby paints nothing
      // for a peer still seated in a live round and the player gets a blank screen.
      rounds?.finish();
      backToLobby();
    },
    () => showResults(true),
  );
  m.broadcast();
}

// ── results ─────────────────────────────────────────────────────────────────────
let resultsMounted = false;
let multi = false;

function showResults(isMulti: boolean): void {
  teardown();
  setPlaying(false);
  multi = isMulti;
  resultsMounted = true;
  const m = match;
  if (!m) {
    showMenu();
    return;
  }
  const rows = m.standings();
  for (const r of rows) if (r.rank === 1) tally[r.seat] = (tally[r.seat] ?? 0) + 1;
  if (m.mySeat !== null) noteBest(m.mode.id, m.breakdown(m.mySeat).total);
  if (isMulti) rounds?.finish();

  content.innerHTML = `<section class="screen results-screen" id="res"></section><div class="menu-actions" id="ract"></div>`;
  renderResults();
  onCleanup(() => {
    resultsMounted = false;
  });
}

function renderResults(): void {
  const m = match;
  if (!m || !resultsMounted) return;
  const box = content.querySelector<HTMLElement>('#res');
  const act = content.querySelector<HTMLElement>('#ract');
  if (!box || !act) return;

  const best = m.mode.scoreBest;
  const rows: ResultRow[] = m.standings().map((st) => {
    const sh = m.state.sheets[st.seat];
    const b = st.breakdown;
    const dropped: number[] = [];
    if (best > 0 && best < 4) [b.ladder, b.tally, b.run, b.weights].forEach((v, i) => (v === 0 ? dropped.push(i) : undefined));
    return {
      seat: st.seat,
      name: m.names[st.seat] ?? `Player ${st.seat + 1}`,
      isSelf: m.mySeat === st.seat,
      isBot: m.botTier !== null && m.mySeat !== st.seat,
      rank: st.rank,
      breakdown: b,
      written: sh.written,
      chain: sh.chain,
      passed: sh.passed,
      left: m.missing.has(st.seat),
      dropped,
    };
  });

  box.innerHTML = resultsHtml({
    rows,
    modeName: m.mode.name,
    round: multi ? roundNo : soloRound,
    tally,
    names: m.names,
    multi,
    fault: m.fault,
    best: m.mySeat !== null ? bestFor(m.mode.id) : 0,
    missed: m.mySeat !== null ? missedNote(m.mode.id, m.seed, m.n, m.state.log, m.mySeat) : '',
  });

  act.innerHTML = multi
    ? `<button class="btn primary" id="again">Play again</button>
       <p class="waiting-note" id="waiting" aria-live="polite"></p>
       <button class="btn" id="lobby">Back to lobby</button>
       <button class="btn ghost" id="menu">Main menu</button>
       <button class="btn ghost results-feedback" id="feedback">Report a problem</button>`
    : `<button class="btn primary" id="again">Play again</button>
       <button class="btn" id="share">Share</button>
       <button class="btn ghost" id="menu">Main menu</button>
       <button class="btn ghost results-feedback" id="feedback">Report a problem</button>`;

  act.querySelector('#feedback')!.addEventListener('click', (e) => window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }));
  act.querySelector('#menu')!.addEventListener('click', () => (multi ? void leaveRoom() : showMenu()));
  if (multi) {
    const again = act.querySelector<HTMLButtonElement>('#again')!;
    again.addEventListener('click', () => {
      rounds?.vote();
      again.disabled = true;
      again.textContent = 'Waiting for the others…';
      refreshResults();
    });
    act.querySelector('#lobby')!.addEventListener('click', () => backToLobby());
    refreshResults();
  } else {
    act.querySelector('#again')!.addEventListener('click', () => startSolo());
    act.querySelector('#share')!.addEventListener('click', () => void share());
  }
}

function refreshResults(): void {
  if (!resultsMounted || !multi) return;
  const waiting = content.querySelector<HTMLElement>('#waiting');
  if (!waiting) return;
  const r = rounds?.state();
  if (r && r.startsInMs !== null) {
    waiting.textContent = `Next round in ${Math.ceil(r.startsInMs / 1000)}s — ${r.votes.length}/${Math.max(r.present.length, 1)} ready`;
  } else if (r && r.votes.length) {
    waiting.textContent = `${r.votes.length}/${Math.max(r.present.length, 1)} ready for another round`;
  } else {
    waiting.textContent = '';
  }
}

setInterval(() => refreshResults(), 400);

async function share(): Promise<void> {
  const m = match;
  if (!m || m.mySeat === null) return;
  const b = m.breakdown(m.mySeat);
  const text = `Clatter · ${m.mode.name} — ${b.total} points\nhttps://clatter.benrichardson.dev`;
  try {
    if (navigator.share) {
      await navigator.share({ text });
      return;
    }
  } catch {
    /* cancelled — fall through to copy */
  }
  try {
    await navigator.clipboard.writeText(text);
    toast('Copied to clipboard');
  } catch {
    toast('Could not copy');
  }
}

function backToLobby(): void {
  teardown();
  match = null;
  activeMatch = null;
  if (net && rounds) showLobby();
  else showMenu();
}

async function leaveRoom(): Promise<void> {
  teardown();
  // Take the room out of the URL on the way out, or a reload — or reopening from a home-screen
  // icon — silently drops the player back into a room they deliberately left.
  clearRoomInUrl();
  lobbyMounted = false;
  match = null;
  activeMatch = null;
  const n = net;
  const r = rounds;
  net = null;
  rounds = null;
  netSend = () => {};
  try {
    mvOff();
  } catch {
    /* the channel may already be gone */
  }
  mvOff = () => {};
  r?.destroy();
  if (n) {
    try {
      await n.leave();
    } catch {
      /* leaving is best-effort */
    }
  }
  showMenu();
}

// ── boot ────────────────────────────────────────────────────────────────────────
function boot(): void {
  hardenViewport();
  mountShell();
  const room = new URL(location.href).searchParams.get('room');
  if (room && normalizeRoomCode(room).length >= 3) showRoomEntry();
  else showMenu();
  window.addEventListener('beforeunload', () => {
    void net?.leave();
  });
}

// Exposed so the browser-verification pass can drive the REAL submit path synchronously — rAF is
// throttled in a hidden tab, and every phone check runs through this. Never read by gameplay.
(window as unknown as { __clatter?: unknown }).__clatter = {
  state(): unknown {
    const m = activeMatch;
    if (!m) return null;
    const s = m.state;
    return {
      mode: m.mode.id,
      turn: s.turn,
      turns: m.mode.turns,
      phase: s.phase,
      active: activeSeat(s),
      pool: s.pool.slice(),
      spill: s.spill.slice(),
      over: m.over,
      fault: m.fault,
      mySeat: m.mySeat,
      n: m.n,
      waiting: m.waitingOn,
      scores: s.sheets.map((_, i) => m.breakdown(i).total),
      sheets: s.sheets.map((sh) => ({
        ladder: sh.ladder.slice(),
        tally: sh.tally.slice(),
        run: sh.run,
        weights: sh.weights.slice(),
        shims: sh.shims,
        written: sh.written,
      })),
      log: s.log.slice(),
      names: m.names.slice(),
    };
  },
  legal(): number[] {
    return activeMatch?.legal() ?? [];
  },
  play(action: number): boolean {
    return activeSubmit ? activeSubmit(action) : false;
  },
  /** One legal action chosen by the shipped evaluator — enough to walk a whole round in a pane. */
  auto(): boolean {
    const m = activeMatch;
    if (!m || m.over) return false;
    const seat = m.mySeat;
    if (seat === null || !m.mine) return m.playBots();
    const a = m.legal();
    if (a.length === 0) return false;
    return activeSubmit ? activeSubmit(a[Math.floor(a.length / 2)]) : false;
  },
  tracks: TRACK_NAME,
};

boot();
